/**
 * CambiarTarjetaModal — la socia cambia su tarjeta SIN SALIR DEL CANAL.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  Ann (06-10-2026): «¿no podemos hacer que este cambio se haga en el CRM?» ·
 *  «que no tenga que saltar a Stripe» · «es muy ordenado así sin salir del canal».
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * 🔴 EL NÚMERO DE TARJETA NO PASA POR NUESTRO SERVIDOR NI POR ESTE CÓDIGO.
 * Las casillas que se ven son un `iframe` servido por Stripe (su Element): el
 * número viaja del navegador de la clienta a Stripe directamente. Aquí solo
 * llega un identificador (`pm_...`) que no sirve para pagar en ningún otro
 * sitio. Por eso la PANTALLA puede ser nuestra sin que el número lo sea — y por
 * eso NO se añade nunca un <input> propio para el número: eso nos metería en
 * PCI y es exactamente lo que este diseño evita.
 *
 * Stripe.js se carga desde js.stripe.com y NO se empaqueta (Stripe lo prohíbe:
 * la librería se actualiza sola para seguir cumpliendo normativa). Se carga solo
 * cuando la clienta abre el modal, no en cada visita al perfil.
 *
 * Si algo falta (clave pública, Stripe caído), este modal NO se abre: el perfil
 * se queda con el botón del portal de Stripe, que ya está en producción. La
 * clienta nunca se queda sin camino.
 */
import React from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './CambiarTarjetaModal.css';

const STRIPE_JS = 'https://js.stripe.com/v3/';

/** Carga Stripe.js una sola vez por pestaña. */
function cargarStripeJs() {
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (!window.__stripeJsPromise) {
    window.__stripeJsPromise = new Promise((resolve, reject) => {
      const ya = document.querySelector(`script[src="${STRIPE_JS}"]`);
      const s = ya || document.createElement('script');
      s.onload = () => (window.Stripe ? resolve(window.Stripe) : reject(new Error('stripe_js_vacio')));
      s.onerror = () => reject(new Error('stripe_js_no_carga'));
      if (!ya) { s.src = STRIPE_JS; s.async = true; document.head.appendChild(s); }
      else if (window.Stripe) resolve(window.Stripe);
    });
  }
  return window.__stripeJsPromise;
}

export default function CambiarTarjetaModal({ API, onCerrar, onGuardada }) {
  // Sus textos viven en miperfil.json y los trae EL MISMO, venga de donde venga.
  // 19-10-2026: dentro del Canal se le pasaba el `t` del namespace 'canal' y la
  // socia veía «productos.tarjetaGuardar» en vez de «Guardar mi tarjeta».
  const { t } = useTranslation('miperfil');
  const [fase, setFase] = React.useState('cargando'); // cargando | lista | guardando | error
  const [error, setError] = React.useState('');
  const cajaRef = React.useRef(null);
  const refs = React.useRef({ stripe: null, card: null, clientSecret: '' });

  React.useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [cfg, prep] = await Promise.all([
          axios.get(`${API}/canal/mi-tarjeta/config`),
          axios.post(`${API}/canal/mi-tarjeta/preparar`),
        ]);
        if (!vivo) return;
        if (!cfg.data?.disponible || !prep.data?.disponible || !prep.data?.client_secret) {
          setError(t('productos.tarjetaNoDisponible'));
          setFase('error');
          return;
        }
        const Stripe = await cargarStripeJs();
        if (!vivo) return;
        const stripe = Stripe(cfg.data.publishable_key);
        const card = stripe.elements({ locale: 'es' }).create('card', {
          hidePostalCode: true,
          style: { base: { fontSize: '16px', color: '#1f2937', '::placeholder': { color: '#9ca3af' } } },
        });
        refs.current = { stripe, card, clientSecret: prep.data.client_secret };
        setFase('lista');
      } catch {
        if (vivo) { setError(t('productos.tarjetaNoDisponible')); setFase('error'); }
      }
    })();
    return () => {
      vivo = false;
      try { refs.current.card?.destroy(); } catch { /* ya desmontado */ }
    };
  }, [API, t]);

  // El Element se monta cuando el hueco EXISTE en pantalla (fase 'lista'), no antes:
  // montarlo sobre un ref vacio deja el formulario en blanco sin dar error.
  React.useEffect(() => {
    if (fase === 'lista' && cajaRef.current && refs.current.card) {
      try { refs.current.card.mount(cajaRef.current); } catch { /* ya montado */ }
    }
  }, [fase]);

  const guardar = async () => {
    const { stripe, card, clientSecret } = refs.current;
    if (!stripe || !card || !clientSecret) return;
    setFase('guardando');
    setError('');
    try {
      // Aqui es donde el banco puede pedirle confirmar (SCA) — una sola vez, como
      // la primera vez que compro. Stripe abre su propia ventana; nosotros solo
      // esperamos. Sin este paso, la cuota mensual pediria confirmacion cada mes.
      const res = await stripe.confirmCardSetup(clientSecret, { payment_method: { card } });
      if (res.error) {
        setError(res.error.message || t('productos.tarjetaError'));
        setFase('lista');
        return;
      }
      const pmId = res.setupIntent?.payment_method;
      if (!pmId) { setError(t('productos.tarjetaError')); setFase('lista'); return; }
      const g = await axios.post(`${API}/canal/mi-tarjeta/guardar`, { payment_method_id: pmId });
      if (!g.data?.ok) { setError(t('productos.tarjetaError')); setFase('lista'); return; }
      onGuardada?.(g.data);
    } catch {
      setError(t('productos.tarjetaError'));
      setFase('lista');
    }
  };

  const guardando = fase === 'guardando';
  return (
    <div className="mp-tarjeta-fondo" role="dialog" aria-modal="true" aria-label={t('productos.tarjetaCambiar')}>
      <div className="mp-tarjeta-modal">
        <h3>{t('productos.tarjetaCambiar')}</h3>
        <p className="mp-tarjeta-calma">{t('productos.tarjetaAyuda')}</p>

        {fase === 'cargando' && <p className="mp-tarjeta-calma">{t('productos.tarjetaAbriendo')}</p>}

        {fase === 'error' && <p className="mp-tarjeta-error">{error}</p>}

        {(fase === 'lista' || guardando) && (
          <>
            <div ref={cajaRef} className="mp-tarjeta-caja" />
            {error && <p className="mp-tarjeta-error">{error}</p>}
            <p className="mp-tarjeta-calma">{t('productos.tarjetaSegura')}</p>
          </>
        )}

        <div className="mp-tarjeta-pies">
          <button type="button" className="mp-tarjeta-btn" onClick={onCerrar} disabled={guardando}>
            {t('productos.tarjetaCancelar')}
          </button>
          {(fase === 'lista' || guardando) && (
            <button type="button" className="mp-tarjeta-btn pri" onClick={guardar} disabled={guardando}>
              {guardando ? t('productos.tarjetaGuardando') : t('productos.tarjetaGuardar')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
